// Meta Pixel tracking utility
// Provides type-safe functions for tracking Meta Pixel events

// Extend Window interface to include fbq
declare global {
  interface Window {
    fbq: (
      action: string,
      event: string,
      params?: Record<string, unknown>
    ) => void;
  }
}

/**
 * Track a Meta Pixel event
 * @param event - The event name (e.g., 'Lead', 'Contact', 'ViewContent')
 * @param params - Optional parameters for the event
 */
export function trackMetaEvent(
  event: string,
  params?: Record<string, unknown>
): void {
  if (typeof window !== 'undefined' && window.fbq) {
    if (params) {
      window.fbq('track', event, params);
    } else {
      window.fbq('track', event);
    }
  }
}

/**
 * Track a Lead event - use when a form is submitted
 * @param contentName - Optional name to identify the form/source
 */
export function trackLead(contentName?: string): void {
  trackMetaEvent('Lead', contentName ? { content_name: contentName } : undefined);
}

/**
 * Track a Contact event - use for contact form submissions
 */
export function trackContact(): void {
  trackMetaEvent('Contact');
}

/**
 * Track a ViewContent event - use when viewing a property listing
 * @param contentId - The property/listing ID
 * @param contentName - The property/listing name
 * @param value - Optional property value/price
 */
export function trackViewContent(
  contentId?: string,
  contentName?: string,
  value?: number
): void {
  trackMetaEvent('ViewContent', {
    content_type: 'property',
    ...(contentId && { content_ids: [contentId] }),
    ...(contentName && { content_name: contentName }),
    ...(value && { value, currency: 'EUR' }),
  });
}

/**
 * Track a Search event - use when searching properties
 * @param searchString - The search query or filters applied
 */
export function trackSearch(searchString?: string): void {
  trackMetaEvent('Search', searchString ? { search_string: searchString } : undefined);
}
